/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  Autocomplete,
  type AutocompleteChangeDetails,
  type AutocompleteChangeReason,
  type AutocompleteProps,
  type AutocompleteRenderInputParams,
  type AutocompleteValue,
  Box,
  Skeleton,
  type SxProps,
  TextField,
  type Theme,
} from '@mui/material';
import { prettifySnakeCase } from '@poweralpha/common';
import { type SyntheticEvent, useCallback, useMemo } from 'react';
import InputKeyValue from '../display';
import type { BaseTextFieldProps } from './BaseTextField';
import { useFieldContext } from './formContext';

const tinyTextFieldSx = {
  '& .MuiInputBase-root': { height: 25, pr: 1 },
  '& .MuiInputLabel-root.Mui-focused': {
    fontSize: 16, // when focused
  },
  '& .MuiInputLabel-root': {
    fontSize: 16,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiInputBase-input': {
    fontSize: 14,
    p: '0 4px', // tight horizontal padding
  },
  '& .MuiChip-root': {
    height: 16,
    fontSize: 10,
    borderRadius: 4,
    margin: '0 1px 1px 3px',
    position: 'relative',
    top: '-4px',
    '& .MuiChip-deleteIcon': {
      fontSize: 12,
      marginLeft: 0.25,
    },
  },
};

const tinySizeProps = {
  slotProps: {
    listbox: {
      sx: {
        '& .MuiAutocomplete-option': {
          minHeight: 28, // ↓ from ~48
          py: 0.25,
          px: 1,
          fontSize: 14,
          lineHeight: 1.2,
        },
      },
    },
  },
  sx: {
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      fontSize: 12,
      '& svg': { fontSize: 12 },
    },
    '& .MuiAutocomplete-listbox': {
      '& .MuiAutocomplete-option': {
        p: 0,
      },
    },
  },
};

const xsTextFieldSx = {
  '& .MuiInputBase-root': { height: 30, pr: 1 },
  '& .MuiInputLabel-root': {
    fontSize: 15,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: 11.3,
  },
  // ↓ Add this
  '& .MuiChip-root': {
    height: 20,
    fontSize: 12,
    borderRadius: 4,
    margin: '0 1px 1px 3px', // top=0, right=1px, bottom=1px, left=3px
    position: 'relative',
    top: '-3px', // ← pushes the chip upward
    '& .MuiChip-deleteIcon': {
      fontSize: 14,
      marginLeft: 0.5,
    },
  },
};

export interface CustomAutocompleteProps<
  T = unknown,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> {
  label?: React.ReactNode;
  variant?: 'outlined' | 'filled' | 'standard';
  isLoading?: boolean;
  handleCreate?: (newValue: string) => void;
  textFieldSx?: SxProps<Theme>;
  displayOnly?: boolean;
  size?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['size'] | 'tiny' | 'xs';
  rightUnit?: string;
  hideErrorText?: boolean;
  'data-testid'?: string;
}

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'size'>,
    Pick<BaseTextFieldProps, 'rightUnit'>,
    CustomAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {}

function FormAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  label,
  variant,
  handleCreate,
  getOptionLabel,
  isLoading,
  textFieldSx,
  displayOnly,
  size,
  rightUnit,
  hideErrorText,
  ...props
}: Readonly<Props<T, Multiple, DisableClearable, FreeSolo>>) {
  const field = useFieldContext();
  const labelRender = label ?? prettifySnakeCase(field.name.split('.').at(-1));

  const handleChange = useCallback(
    (
      event: SyntheticEvent,
      value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<T>,
    ) => {
      if (!handleCreate) {
        field.handleChange(value);
        props.onChange?.(event, value, reason, details);
        return;
      }

      if (value && getOptionLabel && typeof value === 'object' && 'create' in value) {
        setTimeout(() => handleCreate(getOptionLabel(value as T)));
        return;
      }

      field.handleChange(value);
      props.onChange?.(event, value, reason, details);
    },
    [handleCreate, getOptionLabel, field, props.onChange],
  );

  const error = useMemo(() => field.state.meta.errors[0]?.message, [field.state.meta.errors]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      let sx: SxProps<Theme> | undefined = textFieldSx;
      if (size === 'tiny') {
        sx = textFieldSx ? ([tinyTextFieldSx, textFieldSx] as SxProps<Theme>) : tinyTextFieldSx;
      }
      if (size === 'xs') {
        sx = textFieldSx ? ([xsTextFieldSx, textFieldSx] as SxProps<Theme>) : xsTextFieldSx;
      }

      return (
        <TextField
          {...params}
          label={labelRender}
          variant={variant ?? 'outlined'}
          error={error}
          helperText={hideErrorText ? '' : error}
          sx={sx}
        />
      );
    },
    [error, labelRender, size, textFieldSx, variant, hideErrorText],
  );

  const createableOptions = useMemo(() => {
    if (!handleCreate) return {};

    return {
      selectOnFocus: true,
      clearOnBlur: true,
      handleHomeEndKeys: true,
      freeSolo: true,
    };
  }, [handleCreate]);

  const sizeProps = useMemo(() => {
    if (size !== 'tiny') return {};
    return tinySizeProps;
  }, [size]);

  return useMemo(() => {
    if (isLoading) {
      return <Skeleton height={40} />;
    }
    if (displayOnly) {
      // currently only compatible with 'xs' size
      return (
        <InputKeyValue
          value={(getOptionLabel?.(field.state.value as T) ?? field.state.value) as React.ReactNode}
          label={
            error ? (
              <Box
                sx={{
                  color: 'error.main',
                }}
              >
                {labelRender}
              </Box>
            ) : (
              labelRender
            )
          }
          postfixUnits={rightUnit}
          sx={{ pt: 0, pb: 0 }}
        />
      );
    }

    return (
      <Autocomplete
        {...createableOptions}
        {...sizeProps}
        key={JSON.stringify(field.state.value)}
        freeSolo={props.freeSolo}
        size={size !== 'tiny' && size !== 'xs' ? (size ?? 'small') : 'small'}
        fullWidth
        value={field.state.value as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>}
        onChange={handleChange}
        autoSelect={props.autoSelect}
        renderInput={renderInput}
        getOptionLabel={getOptionLabel}
        options={props.options}
        filterOptions={props.filterOptions}
        groupBy={props.groupBy}
        isOptionEqualToValue={props.isOptionEqualToValue}
        multiple={props.multiple}
        noOptionsText={props.noOptionsText}
        onInputChange={props.onInputChange}
        inputValue={props.inputValue}
        disableClearable={props.disableClearable}
        slotProps={props.slotProps}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        sx={props.sx}
        data-testid={props['data-testid'] ?? field.name.split('.').at(-1)}
        disabled={props.disabled}
        renderGroup={props.renderGroup}
        renderOption={props.renderOption}
        getOptionDisabled={props.getOptionDisabled}
        limitTags={props.limitTags}
      />
    );
  }, [
    isLoading,
    field.state.value,
    handleChange,
    renderInput,
    getOptionLabel,
    createableOptions,
    props.freeSolo,
    props.autoSelect,
    props.options,
    props.filterOptions,
    props.groupBy,
    props.isOptionEqualToValue,
    props.onBlur,
    props.onFocus,
    props.onInputChange,
    props.inputValue,
    props.noOptionsText,
    props.disabled,
    props.disableClearable,
    props.slotProps,
    props.limitTags,
    props.sx,
    props['data-testid'],
    size,
    sizeProps,
    field.name,
    props.multiple,
    displayOnly,
    labelRender,
    rightUnit,
    error,
    props.renderGroup,
    props.renderOption,
    props.getOptionDisabled,
  ]);
}

export default FormAutocomplete;
